import React from 'react';
import { Typography, Card, Flex, theme } from 'antd';
import { TeamOutlined, TrophyOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChallengeMode } from '@/types';

const { Text, Title } = Typography;

interface ModeSelectorProps {
  mode: ChallengeMode;
  onModeChange: (mode: ChallengeMode) => void;
}

/**
 * Mode selector component for choosing between One-Time Matching and Knockout Tournament
 */
const ModeSelector: React.FC<ModeSelectorProps> = ({ mode, onModeChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const modes = [
    {
      value: ChallengeMode.OneTimeMatching,
      icon: <TeamOutlined style={{ fontSize: 32 }} />,
      title: t('pages.quizWizard.modeOneTime', 'One-Time Matching'),
      description: t('pages.quizWizard.modeOneTimeDesc', 'Randomly pair participants for single matches'),
      color: token.colorInfo,
      selectedBg: token.colorInfoBg,
      borderColor: token.colorInfoBorder,
    },
    {
      value: ChallengeMode.KnockoutTournament,
      icon: <TrophyOutlined style={{ fontSize: 32 }} />,
      title: t('pages.quizWizard.modeKnockout', 'Knockout Tournament'),
      description: t('pages.quizWizard.modeKnockoutDesc', 'Single-elimination bracket tournament (min. 4 participants)'),
      color: token.colorWarning,
      selectedBg: token.colorWarningBg,
      borderColor: token.colorWarningBorder,
    },
  ];

  return (
    <div style={{ marginBottom: 16, textAlign: 'center' }}>
      <Flex align="center" justify='center' style={{marginBottom: 32}}>
        <ThunderboltOutlined style={{ fontSize: 20, color: '#faad14' }} />
        <Title level={5} style={{ margin: 0 }}>
          {t('pages.quizWizard.challengeMode', 'Challenge Mode')}
        </Title>
      </Flex>
      
      <div style={{ 
        display: 'flex', 
        gap: 16, 
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        {modes.map((modeOption) => {
          const isSelected = mode === modeOption.value;
          return (
            <Card
              key={modeOption.value}
              hoverable
              onClick={() => onModeChange(modeOption.value)}
              style={{
                width: 220,
                cursor: 'pointer',
                background: isSelected ? modeOption.selectedBg : token.colorBgContainer,
                border: `2px solid ${isSelected ? modeOption.borderColor : token.colorBorder}`,
                borderRadius: 12,
                transition: 'all 0.3s ease',
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                boxShadow: isSelected 
                  ? `0 4px 12px ${modeOption.color}40`
                  : '0 2px 8px rgba(0,0,0,0.06)',
              }}
              styles={{
                body: { 
                  padding: 20,
                  textAlign: 'center'
                }
              }}
            >
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: isSelected ? modeOption.color : token.colorFillSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                color: isSelected ? '#fff' : modeOption.color,
                transition: 'all 0.3s ease',
              }}>
                {modeOption.icon}
              </div>
              <Text strong style={{ 
                fontSize: 15, 
                display: 'block',
                color: isSelected ? modeOption.color : token.colorText,
                marginBottom: 4
              }}>
                {modeOption.title}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {modeOption.description}
              </Text>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ModeSelector;
